<template>
  <div class="text-list-page">
    <!-- 列表容器 -->
    <div class="list-container">
      <!-- 暂无数据提示 -->
      <div v-if="localListData.length === 0" class="empty-state">
        <div class="empty-icon">📋</div>
        <p class="empty-text">暂无列表</p>
      </div>

      <!-- 列表项 -->
      <div 
        v-for="(item, index) in localListData" 
        :key="item.id"
        class="list-item"
        :class="{ 'has-image': item.img && item.img.length > 0 }"
        @click="handleItemClick(item)"
      >
        <!-- 图片区域 -->
        <div v-if="item.img && item.img.length > 0" class="item-image">
          <img :src="item.img[0]" :alt="item.title" />
        </div>
        
        <!-- 内容区域 -->
        <div class="item-content">
          <h3 
            class="item-title" 
            :class="{ 'border-blue': index % 2 === 0, 'border-yellow': index % 2 !== 0 }"
          >
            {{ item.title }}
          </h3>
          <p class="item-desc" v-html="item.content"></p>
          <div class="item-date">
            <i class="icon-clock"></i>
            <span>{{ item.create_time }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const props = defineProps({
  listData: {
    type: Array,
    default: () => []
  },
  ident: {
    type: String,
    default: ''
  }
})

const localListData = ref([])

// 监听 props 变化并更新本地数据
watch(() => props.listData, (newVal) => {
  if (newVal && newVal.length > 0) {
    localListData.value = newVal
  }
}, { immediate: true })

const handleItemClick = (item) => {
  // 跳转到详情页动态路由
  router.push({
    name: 'detailPage',
    params: { id: item.id },
    query: { ident: props.ident }
  })
}
</script>

<style scoped>
.text-list-page {
  padding: 20px;
  box-sizing: border-box;
  width: 100%;
  overflow-x: hidden;
}

.list-container {
  max-width: 100%;
  margin: 0 auto;
  background: #fff;
  box-sizing: border-box;
}

.list-item {
  padding: 30px 40px;
  border-bottom: 1px solid #e8e8e8;
  transition: all 0.3s ease;
  position: relative;
  box-sizing: border-box;
}

.list-item.has-image {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  border-bottom: none;
  margin-bottom: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.list-item:hover {
  transform: translateX(8px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.item-image {
  flex-shrink: 0;
  width: 245px;
  height: 150px;
  margin-right: 20px;
  overflow: hidden;
  border-radius: 4px;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.item-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 15px 0;
  padding-left: 12px;
  border-left: 4px solid transparent;
  cursor: pointer;
  line-height: 1.5;
  transition: all 0.3s ease;
}

.item-title:hover {
  color: #1890ff;
  padding-left: 16px;
}

.item-title.border-blue {
  border-left-color: #1890ff;
}

.item-title.border-yellow {
  border-left-color: #faad14;
}

.item-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  margin: 0 0 15px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.item-date {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #999;
}

.icon-clock {
  width: 14px;
  height: 14px;
  margin-right: 6px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>') no-repeat center;
  background-size: contain;
  flex-shrink: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .text-list-page {
    padding: 15px;
  }
  
  .list-item {
    padding: 20px 20px;
  }

  .list-item.has-image {
    flex-direction: column;
  }

  .item-image {
    width: 100%;
    height: 180px;
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .item-title {
    font-size: 15px;
  }
  
  .item-desc {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .text-list-page {
    padding: 10px;
  }
  
  .list-item {
    padding: 15px 15px;
  }

  .item-image {
    height: 150px;
    margin-bottom: 12px;
  }
  
  .item-title {
    font-size: 14px;
    padding-left: 10px;
  }
  
  .item-title:hover {
    padding-left: 12px;
  }
  
  .item-desc {
    font-size: 12px;
  }
  
  .item-date {
    font-size: 12px;
  }
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 16px;
  color: #999;
  margin: 0;
}
</style>
